import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/** The width a chart is drawn at until it has been measured, and when it cannot be. */
const FALLBACK_WIDTH = 720;
/** Narrower than this and the axis labels would run into the bars. */
const MIN_WIDTH = 280;

/**
 * The pixel width of the box a chart sits in, kept up to date as the window or
 * the zoom changes.
 *
 * **Why charts need it:** they used to be drawn on a fixed 720-wide canvas and
 * stretched to fill their card, so on a wide screen the whole picture — text
 * included — was scaled up, and a 250-high chart became 380 pixels tall with
 * oversized labels and fat bars. Drawing on the real width keeps text at its
 * natural size and the height at what was asked for.
 *
 * Without a measurement (a hidden tab, a test with no layout) it answers 720, so
 * a chart always has a usable width.
 */
export function useChartWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  const measure = useCallback((): void => {
    const element = ref.current;
    if (element === null) return;
    // clientWidth, not getBoundingClientRect: the page's own zoom must not change what a chart measures.
    const measured = element.clientWidth;
    if (measured > 0) setWidth(Math.max(MIN_WIDTH, measured));
  }, []);

  // After every render: changing the page's zoom re-renders the screen but does not
  // always tell a resize observer, and an unchanged width sets nothing, so this cannot loop.
  useLayoutEffect(measure);

  // And whenever the box itself changes size — the window, a side panel, a tab opening.
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  return [ref, width];
}
